import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowRight, Hammer } from 'lucide-react'
import { Eyebrow } from '@/components/generals/section-heading'
import { buttonVariants } from '@/components/ui/button'
import { HERO_POINTS } from '@/constants/content'

export const HomeHero: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative overflow-hidden'>
            <div className='mx-auto grid max-w-7xl items-center gap-12 px-5 pt-8 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-10 lg:pt-12 lg:pb-28'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 motion-reduce:animate-none lg:col-span-6'>
                    <Eyebrow>
                        Handcrafted tiny houses on wheels
                    </Eyebrow>

                    <h1 className='mt-6 font-heading text-[2.9rem] leading-[1.02] font-medium tracking-tight text-balance sm:text-6xl xl:text-[4.5rem]'>
                        Small homes, built by hand to go <em className='font-normal text-timber italic'>anywhere</em>.
                    </h1>

                    <p className='mt-7 max-w-xl text-lg leading-relaxed text-pretty text-muted-foreground'>
                        We design and build timber tiny houses for full-time living, weekend escapes and everything in between. Choose one of our models or let us build something entirely yours.
                    </p>

                    <div className='mt-10 flex flex-wrap gap-3'>
                        <Link href='/models' className={buttonVariants({ size: 'lg' })}>
                            Explore our models
                            <ArrowRight className='size-4' />
                        </Link>

                        <Link href='/contact' className={buttonVariants({ variant: 'outline', size: 'lg' })}>
                            Request a quote
                        </Link>
                    </div>

                    <ul className='mt-12 grid gap-4 border-t border-border pt-8 sm:grid-cols-3'>
                        {HERO_POINTS.map(point => (
                            <li key={point.label} className='flex items-center gap-3'>
                                <span className='flex size-10 shrink-0 items-center justify-center rounded-full bg-secondary text-primary'>
                                    <point.icon className='size-[1.125rem]' aria-hidden='true' />
                                </span>

                                <span className='text-sm leading-snug font-medium'>
                                    {point.label}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative animate-in fade-in zoom-in-95 duration-1000 motion-reduce:animate-none lg:col-span-6'>
                    <div className='relative aspect-[4/5] overflow-hidden rounded-[2rem] bg-secondary sm:aspect-[5/4] lg:aspect-[4/5]'>
                        <Image
                            src='/images/hero.jpg'
                            alt="Small timber cabin on a grassy hill under a wide blue sky"
                            fill
                            priority
                            sizes='(min-width: 1280px) 600px, (min-width: 1024px) 50vw, 100vw'
                            className='object-cover object-bottom'
                        />
                    </div>

                    <div className='absolute top-5 left-5 max-w-[17rem] rounded-2xl bg-background/95 p-4 shadow-[0_20px_40px_-20px_rgb(31_43_36/0.45)] backdrop-blur sm:top-7 sm:left-7 sm:p-5'>
                        <div className='flex items-start gap-3'>
                            <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground'>
                                <Hammer className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <p className='font-semibold'>
                                    Built to order
                                </p>

                                <p className='mt-1 text-sm leading-snug text-muted-foreground'>
                                    Every home is designed with you and made by hand in our workshop.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
